import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useMutation, useQuery, UseMutationResult, UseQueryResult } from 'react-query';
import {
  TaxonomyImportResult,
  PresentationTreeResponse,
  ConceptResponse,
  TaxonomySummary,
  AvailableTaxonomyFolder,
  CalculationGroupResponse,
  ExternalReferenceResponse,
  DimensionsForConceptResponse,
} from './types';

export const taxonomyQueryKeys = {
  all: 'TAXONOMIES',
  availableFolders: 'TAXONOMY_AVAILABLE_FOLDERS',
  presentationTree: 'PRESENTATION_TREE',
  concept: 'CONCEPT',
  activeTaxonomy: 'ACTIVE_TAXONOMY',
  calculations: 'CALCULATIONS',
  externalReferences: 'EXTERNAL_REFERENCES',
  dimensionsForConcept: 'DIMENSIONS_FOR_CONCEPT',
  conceptsByIds: 'CONCEPTS_BY_IDS',
  taxonomyLanguages: 'TAXONOMY_LANGUAGES',
} as const;

export function useTaxonomyLanguages(documentSuperId?: number, taxonomyId?: string): UseQueryResult<string[]> {
  return useQuery(
    [taxonomyQueryKeys.taxonomyLanguages, documentSuperId, taxonomyId],
    async () => {
      const url = `/gaby/taxonomy/languages?documentSuperId=${documentSuperId}&taxonomyId=${encodeURIComponent(taxonomyId!)}`;
      const res = await request.get(url);
      return (res?.data as string[]) ?? [];
    },
    {
      enabled: !!documentSuperId && !!taxonomyId,
      staleTime: 30 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function usePresentationTree(documentSuperId?: number, taxonomyId?: string): UseQueryResult<PresentationTreeResponse | null> {
  return useQuery(
    [taxonomyQueryKeys.presentationTree, documentSuperId, taxonomyId],
    async () => {
      const taxonomyParam = taxonomyId ? `&taxonomyId=${taxonomyId}` : '';
      const res = await request.get(`/gaby/taxonomy/presentation-tree?documentSuperId=${documentSuperId}${taxonomyParam}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as PresentationTreeResponse) || null;
    },
    {
      enabled: !!documentSuperId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      onError: ServerErrorHandler,
    }
  );
}

export function useConcept(conceptName: string | null, documentSuperId?: number): UseQueryResult<ConceptResponse | null> {
  return useQuery(
    [taxonomyQueryKeys.concept, conceptName, documentSuperId],
    async () => {
      if (!conceptName || !documentSuperId) return null;

      const url = `/gaby/taxonomy/concepts?name=${encodeURIComponent(conceptName)}&documentSuperId=${documentSuperId}`;

      const res = await request.get(url).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as ConceptResponse) || null;
    },
    {
      enabled: !!conceptName && !!documentSuperId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      onError: ServerErrorHandler,
    }
  );
}

export function useImportTaxonomy(): UseMutationResult<TaxonomyImportResult, unknown, string> {
  return useMutation(
    async (folder: string) => {
      const res = await request.post('/gaby/taxonomy/import', { folder });
      return res?.data as TaxonomyImportResult;
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useAvailableTaxonomyFolders(): UseQueryResult<AvailableTaxonomyFolder[]> {
  return useQuery(
    [taxonomyQueryKeys.availableFolders],
    async () => {
      const res = await request.get('/gaby/taxonomy/available');
      return (res?.data as AvailableTaxonomyFolder[]) || [];
    },
    {
      staleTime: 30 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useImportedTaxonomies(): UseQueryResult<TaxonomySummary[]> {
  return useQuery(
    [taxonomyQueryKeys.all],
    async () => {
      const res = await request.get('/gaby/taxonomy/').catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as TaxonomySummary[]) || [];
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useActiveTaxonomy(documentId: string) {
  return useQuery(
    [taxonomyQueryKeys.activeTaxonomy, documentId],
    async () => {
      const res = await request.get(`/gaby/taxonomy/active?documentId=${encodeURIComponent(documentId)}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as TaxonomySummary) || null;
    },
    {
      onError: ServerErrorHandler,
      enabled: !!documentId,
    }
  );
}

export function useExternalReferences(documentSuperId?: number, conceptName?: string | null): UseQueryResult<ExternalReferenceResponse[] | null> {
  return useQuery(
    [taxonomyQueryKeys.externalReferences, documentSuperId, conceptName],
    async () => {
      if (!documentSuperId || !conceptName) return null;

      const url = `/gaby/taxonomy/external-references?documentSuperId=${documentSuperId}&conceptName=${encodeURIComponent(conceptName)}`;

      const res = await request.get(url).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as ExternalReferenceResponse[]) || null;
    },
    {
      enabled: !!documentSuperId && !!conceptName,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export interface ConceptByIdsItem {
  id: string;
  name: string;
  isMandatory?: boolean;
  conceptType?: unknown;
  labels?: Record<string, Record<string, string>>;
}

export function useConceptsByIds(conceptIds: string[] | undefined): UseQueryResult<ConceptByIdsItem[]> {
  return useQuery(
    [taxonomyQueryKeys.conceptsByIds, [...(conceptIds ?? [])].sort()],
    async () => {
      if (!conceptIds || conceptIds.length === 0) return [];
      const res = await request.post('/gaby/taxonomy/concepts/batch', { conceptIds });
      return (res?.data as ConceptByIdsItem[]) ?? [];
    },
    {
      enabled: !!conceptIds && conceptIds.length > 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useDimensionsForConcept(documentSuperId?: number, conceptName?: string | null): UseQueryResult<DimensionsForConceptResponse | null> {
  return useQuery(
    [taxonomyQueryKeys.dimensionsForConcept, documentSuperId, conceptName],
    async () => {
      if (!documentSuperId || !conceptName) return null;
      const url = `/gaby/taxonomy/dimensions-for-concept?documentSuperId=${documentSuperId}&conceptName=${encodeURIComponent(conceptName)}`;
      const res = await request.get(url).catch((err: { status: number }) => {
        if (err.status === 404) {
          // Concept not in any dimensional network — empty result, not an error
          return null;
        }
        throw err;
      });
      return (res?.data as DimensionsForConceptResponse) || null;
    },
    {
      enabled: !!documentSuperId && !!conceptName,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useCalculations(
  documentSuperId?: number,
  conceptName?: string | null,
  contextId?: string,
  lang?: string,
  viewerTagId?: string
): UseQueryResult<CalculationGroupResponse[] | null> {
  return useQuery(
    [taxonomyQueryKeys.calculations, documentSuperId, conceptName, contextId, lang, viewerTagId],
    async () => {
      if (!documentSuperId || !conceptName) return null;

      let url = `/gaby/taxonomy/calculations?documentSuperId=${documentSuperId}&conceptName=${encodeURIComponent(conceptName)}`;
      if (contextId) {
        url += `&contextId=${encodeURIComponent(contextId)}`;
      }
      if (lang) {
        url += `&lang=${encodeURIComponent(lang)}`;
      }
      if (viewerTagId) {
        url += `&viewerTagId=${encodeURIComponent(viewerTagId)}`;
      }

      const res = await request.get(url).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as CalculationGroupResponse[]) || null;
    },
    {
      enabled: !!documentSuperId && !!conceptName,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}
